import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CreditCard, CheckCircle, Clock, TrendingUp, Search, AlertCircle, Link2, Users, UserCheck, UserPlus, Download, AlertTriangle, Send } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { RenewalCampaignDialog } from '@/components/RenewalCampaignDialog';

const formatDate = (d) => {
  if (!d) return '--';
  try { return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(d)); }
  catch { return d; }
};

const formatCurrency = (v) => `${(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })}`;

const KIND_STYLE = {
  adhesion: 'bg-sky-50 text-sky-700',
  commande: 'bg-violet-50 text-violet-700',
  achat: 'bg-amber-50 text-amber-700',
  don: 'bg-pink-50 text-pink-700',
  evenement: 'bg-emerald-50 text-emerald-700',
  autre: 'bg-gray-100 text-gray-600',
};

const PaymentKindCell = ({ checkout: c }) => {
  const kind = c.payment_kind || 'autre';
  const label = kind === 'commande' ? `Adhésion · ancienne boutique #${c.wc_order_number}` : (c.payment_kind_label || 'Paiement');
  const detail = kind === 'commande' ? (c.wc_product || "Commande de l'ancienne boutique") : (c.item_name || c.subscription_type || '--');
  return (
    <div data-testid={`checkout-kind-${kind}-${c.id}`}>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${KIND_STYLE[kind] || KIND_STYLE.autre}`}>{label}</span>
      <p className="text-xs text-gray-500 mt-1">
        {detail}
        {c.donation_amount > 0 && <span className="text-pink-600"> + don {formatCurrency(c.donation_amount)} €</span>}
        {c.membership_year && kind !== 'achat' && <span className="text-gray-400"> · {c.membership_year}</span>}
      </p>
    </div>
  );
};

const HelloAssoAdminPage = () => {
  const [stats, setStats] = useState(null);
  const [checkouts, setCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [linkFilter, setLinkFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [expiring, setExpiring] = useState([]);
  const [members, setMembers] = useState([]);
  const [linkingId, setLinkingId] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [bulkCreating, setBulkCreating] = useState(false);
  const [creatingId, setCreatingId] = useState(null);
  const [expiringMembers, setExpiringMembers] = useState(null);
  const [showExpiring, setShowExpiring] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderType, setReminderType] = useState(null);
  const [showCampaign, setShowCampaign] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, checkoutsRes, expiryRes] = await Promise.all([
        axios.get(`${API}/helloasso/stats`),
        axios.get(`${API}/helloasso/checkouts`),
        axios.get(`${API}/helloasso/expiry-preview`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setCheckouts(checkoutsRes.data);
      setExpiring(expiryRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des données HelloAsso');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/helloasso/sync`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const res = await axios.post(`${API}/helloasso/reconcile`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de reconciliation');
    } finally {
      setReconciling(false);
    }
  };

  const openLinkDialog = async (checkoutId) => {
    setLinkingId(checkoutId);
    setMemberSearch('');
    try {
      const res = await axios.get(`${API}/users`);
      setMembers(res.data.filter(u => ['ophthalmologist', 'intern'].includes(u.role)));
    } catch { toast.error('Erreur chargement membres'); }
  };

  const handleManualLink = async (userId) => {
    try {
      const res = await axios.post(`${API}/helloasso/link-checkout`, { checkout_id: linkingId, user_id: userId });
      toast.success(res.data.message);
      setLinkingId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de rattachement');
    }
  };

  const handleCreateMember = async (checkoutId) => {
    setCreatingId(checkoutId);
    try {
      const res = await axios.post(`${API}/helloasso/create-member-from-checkout`, { checkout_id: checkoutId });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la creation du compte');
    } finally {
      setCreatingId(null);
    }
  };

  const handleBulkCreate = async () => {
    if (!window.confirm(`Créer des comptes pour les ${unlinkedCount} payeur(s) sans compte ? Cette action est irréversible.`)) return;
    setBulkCreating(true);
    try {
      const res = await axios.post(`${API}/helloasso/bulk-create-members`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'import en masse');
    } finally {
      setBulkCreating(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API}/helloasso/export-csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'helloasso_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export CSV téléchargé');
    } catch { toast.error('Erreur export CSV'); }
  };

  const fetchExpiringMembers = async () => {
    try {
      const res = await axios.get(`${API}/helloasso/expiring-members`);
      setExpiringMembers(res.data);
      setShowExpiring(true);
    } catch { toast.error('Erreur chargement'); }
  };

  const handleSendReminders = async (type, memberIds = []) => {
    const labels = {
      all: 'tous les membres concernés',
      expiring_soon: 'les membres dont l\'adhésion expire bientôt',
      expired: 'les membres avec adhésion expirée',
      selected: `${memberIds.length} membre(s) sélectionné(s)`,
    };
    if (!window.confirm(`Envoyer un email de relance à ${labels[type]} ?`)) return;
    setSendingReminders(true);
    setReminderType(type);
    try {
      const res = await axios.post(`${API}/helloasso/send-reminders`, { type, member_ids: memberIds });
      toast.success(res.data.message);
      fetchExpiringMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi des relances');
    } finally {
      setSendingReminders(false);
      setReminderType(null);
    }
  };

  const filtered = checkouts.filter(c => {
    const matchSearch = searchTerm === '' ||
      (c.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.user_email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchLink = linkFilter === 'all' ||
      (linkFilter === 'linked' && c.user_id) ||
      (linkFilter === 'unlinked' && !c.user_id);
    const matchKind = kindFilter === 'all' || c.payment_kind === kindFilter || (kindFilter === 'adhesion' && c.payment_kind === 'commande');
    return matchSearch && matchStatus && matchLink && matchKind;
  });

  const linkedCount = checkouts.filter(c => c.user_id).length;
  const unlinkedCount = checkouts.filter(c => !c.user_id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#35AFE4]" size={32} />
      </div>
    );
  }

  return (
      <div className="space-y-6" data-testid="helloasso-admin-page">
        <RenewalCampaignDialog open={showCampaign} onOpenChange={setShowCampaign} />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>HelloAsso</h1>
            <p className="text-sm text-gray-500 mt-1">Gestion des paiements et synchronisation HelloAsso</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleExportCSV}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-bold transition-all"
              data-testid="btn-export-csv">
              <Download size={16} /> Export CSV
            </button>
            <button onClick={fetchExpiringMembers}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-xl text-sm font-bold transition-all"
              data-testid="btn-expiring">
              <AlertTriangle size={16} /> Expirations
            </button>
            <button onClick={() => setShowCampaign(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded-xl text-sm font-bold transition-all"
              data-testid="btn-renewal-campaign">
              <Send size={16} /> Relancer avant masquage
            </button>
            {unlinkedCount > 0 && (
              <button
                onClick={handleBulkCreate}
                disabled={bulkCreating}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-bold transition-all disabled:opacity-50"
                data-testid="btn-bulk-create"
              >
                <UserPlus size={16} className={bulkCreating ? 'animate-pulse' : ''} />
                {bulkCreating ? 'Création...' : `Créer ${unlinkedCount} compte(s)`}
              </button>
            )}
            <button
              onClick={handleReconcile}
              disabled={reconciling}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#35AFE4] hover:bg-[#1e9bd4] text-white rounded-xl text-sm font-bold transition-all disabled:opacity-50"
              data-testid="btn-reconcile"
            >
              <Link2 size={16} className={reconciling ? 'animate-spin' : ''} />
              {reconciling ? 'Reconciliation...' : 'Reconcilier'}
            </button>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#49b48c] hover:bg-[#3a9d78] text-white rounded-xl text-sm font-bold transition-all disabled:opacity-50"
              data-testid="btn-sync-helloasso"
            >
              <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Synchronisation...' : 'Synchroniser'}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="stat-total-checkouts">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><CreditCard size={18} className="text-blue-600" /></div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Transactions</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.total_checkouts}</p>
              {stats.counts_by_kind && (
                <p className="text-[11px] text-gray-500 mt-1" data-testid="stat-kind-breakdown">{stats.counts_by_kind.adhesion} adhésions · {stats.counts_by_kind.achat} achats boutique</p>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="stat-completed">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center"><CheckCircle size={18} className="text-emerald-600" /></div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Completees</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="stat-pending">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center"><Clock size={18} className="text-amber-600" /></div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">En attente</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="stat-revenue">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center"><TrendingUp size={18} className="text-violet-600" /></div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Revenus HA</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_revenue)}<span className="text-sm text-gray-400 ml-1">EUR</span></p>
              {stats.revenue_by_kind && (
                <p className="text-[11px] text-gray-500 mt-1 leading-relaxed" data-testid="stat-revenue-breakdown">
                  Adhésions {formatCurrency(stats.revenue_by_kind.adhesion)} € · Boutique {formatCurrency(stats.revenue_by_kind.achat)} €
                  {stats.revenue_by_kind.don > 0 && <> · Dons {formatCurrency(stats.revenue_by_kind.don)} €</>}
                </p>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="stat-linked">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-cyan-50 flex items-center justify-center"><UserCheck size={18} className="text-cyan-600" /></div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Relies</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{linkedCount}<span className="text-sm text-gray-400 font-normal ml-1">/ {checkouts.length}</span></p>
            </div>
          </div>
        )}

        {/* Listings that the next daily check will hide */}
        {expiring.length > 0 && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl px-5 py-3" data-testid="expiry-preview-banner">
            <p className="text-sm text-rose-800 flex items-center gap-2">
              <AlertCircle size={16} className="shrink-0" />
              <span><strong>{expiring.length} praticien(s)</strong> sans adhésion valide (fin de la période de grâce d'un mois) : leurs fiches seront masquées lors du contrôle quotidien de 6h, puis réactivées dès leur paiement.</span>
            </p>
            <p className="text-xs text-rose-700 mt-1.5 pl-6" data-testid="expiry-preview-names">
              {expiring.map(u => u.name).join(', ')}
            </p>
          </div>
        )}

        {/* Info banner */}
        {unlinkedCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-center gap-3" data-testid="unlinked-banner">
            <AlertCircle size={18} className="text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">
              <strong>{unlinkedCount} paiement(s)</strong> ne sont pas reliés à un membre. 
              Utilisez <strong>"Reconcilier"</strong> pour un matching auto par nom, <UserPlus size={12} className="inline" /> pour créer un compte, ou <Link2 size={12} className="inline" /> pour rattacher manuellement.
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom ou email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4]"
              data-testid="search-helloasso"
            />
          </div>
          <select
            value={kindFilter}
            onChange={e => setKindFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/20"
            data-testid="filter-kind"
          >
            <option value="all">Tous les types</option>
            <option value="adhesion">Toutes les adhésions</option>
            <option value="commande">Adhésions (ancienne boutique) seulement</option>
            <option value="achat">Achats boutique (rapports…)</option>
            <option value="don">Dons</option>
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/20"
            data-testid="filter-status"
          >
            <option value="all">Tous les statuts</option>
            <option value="completed">Completees</option>
            <option value="pending">En attente</option>
          </select>
          <select
            value={linkFilter}
            onChange={e => setLinkFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/20"
            data-testid="filter-link"
          >
            <option value="all">Tous</option>
            <option value="linked">Relies a un membre</option>
            <option value="unlinked">Non reliés</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="helloasso-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-5 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Payeur HelloAsso</th>
                  <th className="text-left px-5 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Type</th>
                  <th className="text-left px-5 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Montant</th>
                  <th className="text-left px-5 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Statut</th>
                  <th className="text-left px-5 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left px-5 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider">Membre lie</th>
                  <th className="text-left px-5 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider w-20"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-400">
                      <AlertCircle size={32} className="mx-auto mb-3 opacity-50" />
                      <p>Aucune transaction trouvée</p>
                    </td>
                  </tr>
                ) : (
                  filtered.map(checkout => (
                    <tr key={checkout.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`checkout-row-${checkout.id}`}>
                      <td className="px-5 py-3.5">
                        <p className="font-semibold text-gray-900 text-sm">{checkout.user_name || '--'}</p>
                        <p className="text-xs text-gray-400">{checkout.user_email || '--'}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <PaymentKindCell checkout={checkout} />
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="font-bold text-gray-900">{formatCurrency(checkout.amount)}<span className="text-gray-400 font-normal ml-0.5">EUR</span></span>
                      </td>
                      <td className="px-5 py-3.5">
                        {checkout.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">
                            <CheckCircle size={12} /> OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-bold">
                            <Clock size={12} /> Attente
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-gray-500 text-xs">{formatDate(checkout.created_at)}</td>
                      <td className="px-5 py-3.5">
                        {checkout.user_id ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-700 text-xs font-bold">
                            <UserCheck size={12} /> Relie
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Non relie</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {!checkout.user_id && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleCreateMember(checkout.id)}
                              disabled={creatingId === checkout.id}
                              className="p-1.5 hover:bg-emerald-50 rounded-lg text-gray-400 hover:text-emerald-600 transition-colors"
                              title="Créer un compte membre"
                              data-testid={`btn-create-member-${checkout.id}`}
                            >
                              <UserPlus size={16} className={creatingId === checkout.id ? 'animate-pulse' : ''} />
                            </button>
                            <button
                              onClick={() => openLinkDialog(checkout.id)}
                              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-[#35AFE4] transition-colors"
                              title="Rattacher à un membre existant"
                              data-testid={`btn-link-${checkout.id}`}
                            >
                              <Link2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filtered.length > 0 && (
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/30 text-xs text-gray-400">
              {filtered.length} transaction(s) sur {checkouts.length} | {linkedCount} relie(s), {unlinkedCount} non relie(s)
            </div>
          )}
        </div>

        {/* Manual Link Dialog */}
        {linkingId && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="link-dialog">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900">Rattacher a un membre</h3>
                <p className="text-sm text-gray-500 mt-1">Sélectionnez le membre correspondant a ce paiement</p>
              </div>
              <div className="px-6 py-3 border-b border-gray-100">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher un membre..."
                    value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/20"
                    data-testid="member-search-input"
                  />
                </div>
              </div>
              <div className="overflow-y-auto max-h-[50vh]">
                {members
                  .filter(m => memberSearch === '' ||
                    `${m.first_name} ${m.last_name}`.toLowerCase().includes(memberSearch.toLowerCase()) ||
                    (m.email || '').toLowerCase().includes(memberSearch.toLowerCase())
                  )
                  .slice(0, 30)
                  .map(m => (
                    <button
                      key={m.id}
                      onClick={() => handleManualLink(m.id)}
                      className="w-full text-left px-6 py-3 hover:bg-blue-50 border-b border-gray-50 transition-colors flex items-center justify-between"
                      data-testid={`link-member-${m.id}`}
                    >
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{m.first_name} {m.last_name}</p>
                        <p className="text-xs text-gray-400">{m.email}</p>
                      </div>
                      <Link2 size={14} className="text-gray-300" />
                    </button>
                  ))
                }
              </div>
              <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setLinkingId(null)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium"
                  data-testid="btn-cancel-link"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Expiring Members */}
        {showExpiring && expiringMembers && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowExpiring(false)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>Adhésions à surveiller</h2>
                  <p className="text-xs text-gray-400">{expiringMembers.expiring_soon_count} expire(nt) bientôt, {expiringMembers.expired_count} expiré(es)</p>
                </div>
                <button onClick={() => setShowExpiring(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
              </div>

              {/* Boutons de relance globaux */}
              {(expiringMembers.expiring_soon_count > 0 || expiringMembers.expired_count > 0) && (
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-2">
                  <button onClick={() => handleSendReminders('all')} disabled={sendingReminders}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e3a5f] hover:bg-[#2a5a8f] text-white text-xs font-bold rounded-lg transition-all disabled:opacity-50"
                    data-testid="btn-remind-all">
                    {sendingReminders && reminderType === 'all' ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                    Relancer tout le monde ({expiringMembers.expiring_soon_count + expiringMembers.expired_count})
                  </button>
                  {expiringMembers.expiring_soon_count > 0 && (
                    <button onClick={() => handleSendReminders('expiring_soon')} disabled={sendingReminders}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg transition-all disabled:opacity-50"
                      data-testid="btn-remind-expiring">
                      {sendingReminders && reminderType === 'expiring_soon' ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                      Relancer expire bientôt ({expiringMembers.expiring_soon_count})
                    </button>
                  )}
                  {expiringMembers.expired_count > 0 && (
                    <button onClick={() => handleSendReminders('expired')} disabled={sendingReminders}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg transition-all disabled:opacity-50"
                      data-testid="btn-remind-expired">
                      {sendingReminders && reminderType === 'expired' ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                      Relancer expirés ({expiringMembers.expired_count})
                    </button>
                  )}
                </div>
              )}

              <div className="p-6 overflow-y-auto max-h-[55vh]">
                {expiringMembers.expiring_soon_count > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-bold text-amber-600 mb-3 flex items-center gap-2"><AlertTriangle size={14} /> Expirent dans les 7 prochains jours</h3>
                    <div className="space-y-2">
                      {expiringMembers.expiring_soon.map(m => (
                        <div key={m.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-100">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{m.first_name} {m.last_name}</p>
                            <p className="text-xs text-gray-400">{m.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {m.last_reminder_sent && (
                              <span className="text-[10px] text-gray-400" title={`Dernière relance: ${formatDate(m.last_reminder_sent)}`}>Relancé</span>
                            )}
                            <button onClick={() => handleSendReminders('selected', [m.id])} disabled={sendingReminders}
                              className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold rounded-lg transition-all disabled:opacity-50"
                              data-testid={`btn-remind-${m.id}`}>
                              Relancer
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {expiringMembers.expired_count > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-red-500 mb-3 flex items-center gap-2"><AlertCircle size={14} /> Adhésions expirées</h3>
                    <div className="space-y-2">
                      {expiringMembers.expired.slice(0, 30).map(m => (
                        <div key={m.id} className="flex items-center justify-between p-3 bg-red-50 rounded-xl border border-red-100">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{m.first_name} {m.last_name}</p>
                            <p className="text-xs text-gray-400">{m.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {m.last_reminder_sent && (
                              <span className="text-[10px] text-gray-400" title={`Dernière relance: ${formatDate(m.last_reminder_sent)}`}>Relancé</span>
                            )}
                            <button onClick={() => handleSendReminders('selected', [m.id])} disabled={sendingReminders}
                              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold rounded-lg transition-all disabled:opacity-50"
                              data-testid={`btn-remind-${m.id}`}>
                              Relancer
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {expiringMembers.expired_count === 0 && expiringMembers.expiring_soon_count === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <CheckCircle size={28} className="mx-auto mb-2 text-emerald-300" />
                    <p className="text-sm">Toutes les adhésions sont à jour !</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default HelloAssoAdminPage;
